/**
 * Public branded types and outcome contracts for the parser-engine boundary.
 *
 * @remarks
 * The parser is game-agnostic: it never defines which intents exist or what an
 * entity is. Callers inject vocabulary (whose canonical tokens become
 * {@link IntentToken} / {@link DirectionToken} values) and per-parse entity
 * catalogs (whose references are {@link EntityRef} values). The brands keep
 * those caller-owned strings from mixing with arbitrary text inside the
 * pipeline — without importing anything (this package has zero dependencies by
 * contract; the brands are hand-rolled rather than Zod's).
 *
 * The outcome contracts ({@link ParseOutcome}, {@link ParsedCommand},
 * {@link ParseDiagnostics}, {@link ClarificationRequest}) are plan §2.1's
 * shapes: per-clause command proposals, an honest question when selection
 * would be a guess, or teaching diagnostics. Language proposes; the consumer
 * disposes.
 *
 * @packageDocumentation
 */

import type { PronounWord } from './lexicon/builtin-function-words.js';

declare const parserBrandSymbol: unique symbol;

/**
 * A nominally-branded string. The brand exists only at the type level and
 * erases completely at runtime.
 */
export type ParserBranded<BrandName extends string> = {
	readonly [parserBrandSymbol]: BrandName;
} & string;

/**
 * A candidate the consumer should offer when asking "which one?". The parser
 * knows refs and tokens, never display names — the consumer renders the
 * question from its own world data (plan §2.1).
 */
export interface ClarificationCandidate {
	/** The candidate's catalog reference. */
	readonly entityRef: EntityRef;
	/** The phrase tokens that matched this candidate: consumed qualifiers first, then the matched noun/alias. */
	readonly matchedTokens: readonly string[];
}

/** A question the parser needs answered before committing to one reading (plan §2.1). */
export interface ClarificationRequest {
	/** The contenders, best-first, capped (default 4). */
	readonly candidates: readonly ClarificationCandidate[];
	/** The ambiguous clause, folded form. */
	readonly clauseText: string;
	readonly kind: 'which_entity';
}

/** A canonical direction token from the injected vocabulary (e.g. `north`, `up`). */
export type DirectionToken = ParserBranded<'DirectionToken'>;

/** An opaque reference to a caller-supplied entity in the parse catalog. */
export type EntityRef = ParserBranded<'EntityRef'>;

/** A canonical intent token from the injected vocabulary (e.g. `take`, `inspect`). */
export type IntentToken = ParserBranded<'IntentToken'>;

/**
 * One referable entity in a per-parse catalog (architecture.md §4.4).
 *
 * @remarks
 * The parser stays standalone by accepting what is currently referable as
 * data instead of querying a world. `engine-runtime` derives this catalog
 * each turn from visibility/scope rules; the demo terminal hand-authors a
 * static one. Entity resolution (M4) matches nouns/aliases, filters by
 * adjectives, and weights survivors by salience.
 */
export interface ParseCandidateEntity {
	/** Adjectives that select this entity ("damaged", "coolant"). */
	readonly adjectives: readonly string[];
	/** Alternate nouns ("manifold", "pipes"). */
	readonly aliases: readonly string[];
	/**
	 * D205: the words this entity's OWN prose uses about it — its description,
	 * every reading — folded as player input is folded (`wordsOfProse`, the
	 * one tokenization on both sides of the comparison). A modifier the adjectives do not carry is still
	 * accepted when the page said it: the lane reads "an old man stands under
	 * the breadfruit tree", and `examine the old man` is the player typing the
	 * game's words back. Optional: a catalog that carries none resolves by
	 * adjectives alone, exactly as before.
	 */
	readonly descriptionWords?: readonly string[];
	readonly entityRef: EntityRef;
	/**
	 * D252: somebody an order may be given to — `tell <it> to <clause>`,
	 * `<it>, <clause>`. The parser reads an order only when its addressee
	 * resolves to an entry marked so; which entries are bodies is the
	 * consumer's to say, and a catalog that marks none never reads an order.
	 */
	readonly isAddressable?: true;
	/**
	 * D261: the entry this thing is INSIDE, when it is inside one — so
	 * `take the canister from locker m-12` can settle which canister by the
	 * locker. The consumer's to say; a catalog that says nothing settles a
	 * located object exactly as before, by asking.
	 */
	readonly containerRef?: EntityRef;
	/**
	 * D134: WHERE the thing is, for the verb to weigh — `take` prefers what is
	 * present, `drop` what is carried, and a verb with no preference asks
	 * when both answer. Optional: a catalog that declares no locations gets
	 * salience-only selection, exactly as before.
	 */
	readonly location?: EntityLocation;
	/** Primary nouns ("manifold"). */
	readonly nouns: readonly string[];
	/**
	 * D245: the one pronoun word that stands for this entity — `her` for a
	 * body the consumer calls she, `him` for he, `them` for they, `it` for a
	 * body that is an it. An entry that declares one answers to that word
	 * alone, so `it` never means the woman who was just spoken of; an entry
	 * that declares none (a thing) answers to every pronoun, exactly as
	 * before. Optional: a catalog that declares no pronouns resolves by
	 * recency alone (see `resolve-pronoun.ts`).
	 */
	readonly pronoun?: PronounWord;
	/**
	 * Scope weighting among survivors — the room itself and the player's
	 * hands below every real thing (D41, D63). Since D134 it no longer
	 * encodes location: that is {@link ParseCandidateEntity.location}'s job,
	 * and the verb's preference decides what location is worth.
	 */
	readonly salience: number;
}

/**
 * Where a referable thing is, from the player's side of the sentence (D134):
 * in hand, loose in the room, fixed in the room, the room itself, the
 * player's own body (the D63 hands) — or, since D226 (E7), `absent`: a thing
 * the player knows of and cannot reach from here, referable as a TOPIC. The
 * parser resolves it like any other entry and says where it is; what a
 * topic may be the object of is the consumer's rule, never the parser's.
 */
export type EntityLocation = 'absent' | 'body' | 'carried' | 'fixture' | 'present' | 'room';


/**
 * One parsed clause: a structured command proposal (plan §2.1). The parser
 * proposes; the consumer validates against its world and disposes.
 */
export interface ParsedCommand {
	/**
	 * D252: the body this clause was said TO — `tell TALLY to follow me`,
	 * `TALLY, follow me`. The rest of the command is the order: its verb and
	 * its slots are what the body was told to do, never what the player does.
	 */
	readonly addresseeRef?: EntityRef;
	/** D252: the addressee as the player typed it (its head word), for a reply that names them by it. */
	readonly addresseeSurface?: string;
	/** The clause this command came from, folded form — for transcripts and correction echoes. */
	readonly clauseText: string;
	/** Composed multiplicatively per plan §6.1; 1.0 = every token matched canonically. */
	readonly confidence: number;
	/** Fuzzy fixes applied to this clause's tokens, with source spans (applied from M5; empty until then). */
	readonly corrections: readonly TokenCorrection[];
	readonly direction?: DirectionToken;
	readonly directObjectRef?: EntityRef;
	/**
	 * D226 (E20): what the player TYPED for the direct object's head — the
	 * noun or alias that matched, the pronoun, or the plural word — where
	 * {@link ParsedCommand.directObjectRef} is the thing it resolved to. The
	 * D26 principle for nouns, as `intentSurface` is for the verb: a refusal
	 * about a thing that answers to many words (the room, through its own
	 * nouns) can quote the one the player used. Present whenever the ref (or
	 * the set) is.
	 */
	readonly directObjectSurface?: string;
	/**
	 * D217: the direct object when the player named a SET — a plural naming
	 * two or more things (`examine open jars`), every member in catalog order.
	 * Present instead of {@link ParsedCommand.directObjectRef}, never beside
	 * it. The parser says who the set is; how a set is answered (one line for
	 * the set, each member in turn, or each up to a count) is the consumer's.
	 */
	readonly directObjectSetRefs?: readonly EntityRef[];
	readonly indirectObjectRef?: EntityRef;
	/** D226 (E20): what the player typed for the indirect object's head — see {@link ParsedCommand.directObjectSurface}. */
	readonly indirectObjectSurface?: string;
	readonly intent: IntentToken;
	/**
	 * What the player actually TYPED for the verb — "break open", "grab",
	 * "pick up" — where {@link ParsedCommand.intent} is the canonical token.
	 * Consumers echo this so a refusal quotes the player rather than the
	 * vocabulary's internal name (D36; the D26 principle applied to success).
	 */
	readonly intentSurface: string;
	/**
	 * D56: the phrasal verb's particle, canonicalized — `on` for every one of
	 * "turn on the kettle", "turn the kettle on", "switch it on".
	 *
	 * Present only when the intent declared particles and one was typed. It is
	 * reported separately from {@link ParsedCommand.intent} rather than folded
	 * into it because `turn on` and `turn off` are the SAME verb pointed two
	 * ways: consumers narrow on the pair, and the vocabulary keeps one entry
	 * instead of two near-duplicates.
	 */
	readonly particle?: string;
	/** Surfaced so UIs can echo "(the keycard)" when "it" was resolved. Present only when a pronoun resolved. */
	readonly pronounResolutions?: readonly PronounResolution[];
	/** Adjectives consumed while resolving entities: direct-object phrase first, then the bound phrase. */
	readonly qualifiers: readonly string[];
	/**
	 * D133: HOW the parser read this clause when the imperative grammar did not
	 * — the shape-ladder rung that produced it. Absent for every grammar-parsed
	 * command. A consumer that must know the player named a thing rather than
	 * commanded one (the D37 "what would you like done with it?" follow-up)
	 * reads it here instead of re-parsing the text to find out.
	 */
	readonly rescuedAs?: ShapeRescueKind;
	readonly toolRef?: EntityRef;
	/** D226 (E20): what the player typed for the tool's head — see {@link ParsedCommand.directObjectSurface}. */
	readonly toolSurface?: string;
}

/**
 * The shape-ladder rungs that produce COMMANDS (D29, D31, D133): a question
 * routed onto an information intent, a bearing with resolved chatter kept as
 * movement, and a bare noun read as a look at the thing it names.
 */
export type ShapeRescueKind = 'bare_noun' | 'direction_with_chatter' | 'question';

/**
 * Why an input failed to parse (plan §2.1) — enough structure for a consumer
 * to teach, in-world, instead of shrugging.
 *
 * D24 (2026-08-13): the two `understood*` fields preserve what the grammar
 * DID recognize before it failed, so a teach can say "LOOK takes no object —
 * try INSPECT \<thing\>" instead of dumping tokens. Both are absent when no
 * head intent was recognized.
 */
export interface ParseDiagnostics {
	/**
	 * The folded text of the CLAUSE that failed, when a specific clause did
	 * (absent for blank input and the clause-count cap, where no single clause
	 * is at fault). Consumers that park a question against the failing command
	 * need the clause, never the whole input — a parked whole input re-runs
	 * its prefix when the answer splices (the F5 defect, 2026-08-22).
	 */
	readonly failedClauseText?: string;
	readonly failureKind: ParseFailureKind;
	/** Close-but-not-chosen vocabulary suggestions (fuzzy near-ties, M5). */
	readonly nearMissSuggestions?: readonly string[];
	/** The recognized head intent's object policy, when a head intent was recognized (D24). */
	readonly understoodIntentObjectPolicy?: 'forbidden' | 'optional' | 'required';
	/** The canonical head intent the clause DID have, when one was recognized (D24). */
	readonly understoodIntentToken?: string;
	/** The folded token texts the parser could not place. */
	/**
	 * D36, present on `unsupported_action`: the entity the unknown verb was
	 * aimed at, when the player named one the catalog knows.
	 */
	readonly attemptedObjectRef?: EntityRef;
	/**
	 * D227, present beside {@link ParseDiagnostics.attemptedObjectRef}: the
	 * words the player used for that thing — the name that matched, as typed.
	 * The E20 principle for a failure: a refusal about a thing that answers
	 * to many words (the room, through its own nouns) quotes the one the
	 * player used, so `follow the road` is refused about "the road".
	 */
	readonly attemptedObjectSurface?: string;
	/**
	 * D36, present on `unsupported_action`: the word the player used as a VERB.
	 * Structurally detected — an unrecognized word leading a clause whose
	 * remainder resolves is a verb attempt, whatever the word happens to be, so
	 * no vocabulary list has to enumerate it.
	 */
	readonly attemptedVerbSurface?: string;
	/**
	 * D28, present only on `no_intent_recognized`: whether the input contains
	 * ANY word the parser recognizes as language (vocabulary, catalog,
	 * stopwords, filler, pronouns, or the lexicon's `recognitionWords`) — the
	 * register evidence separating "Am I strong?" from keyboard mash.
	 */
	readonly resemblesLanguage?: boolean;
	readonly unmatchedTokens: readonly string[];
	/**
	 * D212, present on `verb_corrected`: the clause as the fuzzy rung would
	 * have read it — the head word corrected into the verb it was one edit
	 * from (`wake the drum` → `take the drum`). The parser does not RUN that
	 * reading: a correction into a VERB is a correction into an ACT, and an
	 * act the player did not type is theirs to confirm. `unmatchedTokens`
	 * carries the word as typed. Noun-position corrections still execute —
	 * the entity they resolve to is the check.
	 */
	readonly correctedClauseText?: string;
	/**
	 * D48, present on `entity_not_found`: the head intent was matched through a
	 * QUESTION-FORM multiword alias — one whose first word the vocabulary
	 * declares an interrogative leader (`what is`, `where is`, `how is`).
	 *
	 * @remarks
	 * Report only. The parser makes no judgement about registers here; it
	 * records how it read the clause and leaves the consequence to the
	 * consumer, which is the same contract every other field on this interface
	 * keeps.
	 *
	 * Why it exists: those aliases make a question PARSE, so a question whose
	 * subject resolves to nothing fails as a missing entity and never reaches
	 * the D29 shape ladder, which only ever sees clauses the grammar could not
	 * match. The register is lost to a successful parse rather than a failed
	 * one, and this flag is what lets a consumer notice.
	 */
	readonly wasQuestionFormIntent?: true;
}

/**
 * The documented ways an input can fail (plan §2.1), as a value — R12's
 * occurrence-matcher validation checks authored `failureKind`s against this,
 * and deriving the type from the list keeps the two from drifting.
 */
export const PARSE_FAILURE_KINDS = [
	'blank_input',
	'entity_not_found',
	'no_intent_recognized',
	'question_not_understood',
	'social_input',
	'statement_not_understood',
	'structure_not_understood',
	'unbound_preposition',
	'unsupported_action',
	'verb_corrected',
] as const;

/** The documented ways an input can fail (plan §2.1). */
export type ParseFailureKind = (typeof PARSE_FAILURE_KINDS)[number];

/**
 * The result of parsing one input (plan §2.1): per-clause command proposals,
 * a clarification, or diagnostics. `parsedPrefix` carries the clauses BEFORE
 * the failing one that parsed cleanly — the consumer may run them or reject
 * the input wholesale; the parser cannot know which is kinder.
 */
export type ParseOutcome =
	| {
		readonly clarification: ClarificationRequest;
		readonly originalInput: string;
		readonly parsedPrefix: readonly ParsedCommand[];
		/**
		 * The folded texts of clauses AFTER the one that asked, never parsed
		 * (§8.8, 2026-08-23): a stopped chain owes the consumer the names of
		 * what it abandoned, so silence is a choice rather than a blind spot.
		 * Absent when the asking clause was the last.
		 */
		readonly remainingClauseTexts?: readonly string[];
		readonly type: 'needs_clarification';
	}
	| {
		readonly commands: readonly ParsedCommand[];
		readonly originalInput: string;
		readonly type: 'parsed';
	}
	| {
		readonly diagnostics: ParseDiagnostics;
		readonly originalInput: string;
		readonly parsedPrefix: readonly ParsedCommand[];
		/** The folded texts of clauses after the failing one, never parsed (§8.8) — see the clarification arm. */
		readonly remainingClauseTexts?: readonly string[];
		readonly type: 'unrecognized';
	};

/**
 * One fuzzy length tier (plan §6.2): tokens at least `minTokenLength`
 * characters long may receive corrections up to `maxEditDistance` edits.
 * Overlapping tiers combine by taking the largest allowance.
 */
export interface FuzzyLengthThreshold {
	readonly maxEditDistance: number;
	readonly minTokenLength: number;
}

/** Caller-tunable parsing knobs (plan §2). All optional; defaults are documented per field. */
export interface ParserOptions {
	/** Clause outcomes whose composed confidence falls below this floor become `unrecognized`. Default 0.3. */
	readonly confidenceFloor?: number;
	/** Fuzzy length tiers (plan §6.2). Default: <4 none, 4–6 distance ≤ 1, 7+ distance ≤ 2. */
	readonly fuzzyThresholds?: readonly FuzzyLengthThreshold[];
	/**
	 * D133: read each clause as a NOUN PHRASE before the imperative grammar
	 * gets it. For an answer slot — a question that already supplied the verb
	 * — "pry bar" is the name of the bar, never the verb `pry` aimed at it
	 * (D64); the whole-phrase reading wins wherever it resolves or ties, and
	 * the grammar judges only what it declines. Default off: in command
	 * position a parsing verb outranks a name that happens to lead with one.
	 */
	readonly readAsNounPhrase?: boolean;
}

/**
 * One pronoun resolved against the recency list (plan §4), surfaced for UI
 * echoes ("(the keycard)").
 */
export interface PronounResolution {
	/** What the pronoun resolved to: the recency list's most recent ref. */
	readonly entityRef: EntityRef;
	/** The pronoun word as typed (`it`, `them` — a `PronounWord`). */
	readonly pronoun: string;
	/** Where the pronoun sat in its folded clause. */
	readonly sourceSpan: SourceSpan;
}

/**
 * A half-open character range (`end` exclusive) into the FOLDED clause text.
 *
 * @remarks
 * Spans are relative to the clause a token came from, not the raw player
 * input — Unicode folding (NFKC) can change offsets unpredictably, so the
 * folded clause is the stable reference frame for correction echoes and
 * diagnostics.
 */
export interface SourceSpan {
	readonly end: number;
	readonly start: number;
}

/** One word of a folded clause, with its position preserved for diagnostics and correction echoes. */
export interface Token {
	readonly sourceSpan: SourceSpan;
	readonly text: string;
}

/** One fuzzy correction applied to a token (plan §6.2; applied from M5), with its span for UI echo. */
export interface TokenCorrection {
	/** The vocabulary/catalog word the token was corrected to. */
	readonly correctedText: string;
	/** The Damerau–Levenshtein distance of the fix (1 or 2 — the policy caps at 2). */
	readonly editDistance: number;
	/** The token as the player typed it, folded. */
	readonly originalText: string;
	readonly sourceSpan: SourceSpan;
}

/**
 * Brand a non-empty string as a {@link DirectionToken}.
 *
 * @param value - The canonical direction token from the caller's vocabulary
 * @returns The same string, branded
 * @throws TypeError if `value` is empty or whitespace-only (programmer error — tokens come from compiled vocabulary)
 */
export const toDirectionToken = (value: string): DirectionToken => {
	assertNonBlankTokenValue(value, 'DirectionToken');

	return value as DirectionToken;
};

/**
 * Brand a non-empty string as an {@link EntityRef}.
 *
 * @param value - The caller-supplied entity reference
 * @returns The same string, branded
 * @throws TypeError if `value` is empty or whitespace-only (programmer error — refs come from the caller's catalog)
 */
export const toEntityRef = (value: string): EntityRef => {
	assertNonBlankTokenValue(value, 'EntityRef');

	return value as EntityRef;
};

/**
 * Brand a non-empty string as an {@link IntentToken}.
 *
 * @param value - The canonical intent token from the caller's vocabulary
 * @returns The same string, branded
 * @throws TypeError if `value` is empty or whitespace-only (programmer error — tokens come from compiled vocabulary)
 */
export const toIntentToken = (value: string): IntentToken => {
	assertNonBlankTokenValue(value, 'IntentToken');

	return value as IntentToken;
};

/**
 * Shared guard for the branding factories: token values originate in compiled
 * vocabulary or caller catalogs, so a blank one is a programmer error — and
 * per the result-typed-API rule (architecture.md §3.3), programmer errors
 * throw rather than flow.
 *
 * @param value - The candidate token value
 * @param brandName - Which brand the caller was constructing (for the error message)
 * @throws TypeError if `value` is empty or whitespace-only
 */
const assertNonBlankTokenValue = (value: string, brandName: string): void => {
	if (value.trim() === '') {
		throw new TypeError(`A ${brandName} must be a non-blank string; received ${JSON.stringify(value)}`);
	}
};
